import { useMemo, useState, type ChangeEvent } from "react";
import { useQuery } from "@apollo/client/react";
import { formatOneworld } from "@app/components/oneworld-word";
import { useEventsAdminDemo } from "@app/lib/events-admin-demo-context";
import { GET_CLIENTS } from "@app/graphql/events-admin.queries";
import {
  CloseIcon,
  PlusIcon,
  SearchIcon,
  UserGroupIcon,
} from "@app/lib/events-admin-icons";
import { Controller } from "react-hook-form";
import { useNavigate } from "react-router";
import { z } from "zod";
import {
  Dialog,
  Modal,
  ModalOverlay,
  Button,
  ValidatedForm,
  InputBase,
  ValidatedInput,
  Select,
} from "@its-engineering-and-development/ui-foundation/components";
import {
  createFormSchema,
  schemas,
  useValidatedForm,
} from "@its-engineering-and-development/ui-foundation/utils";

const addClientSchema = createFormSchema({
  name: schemas.sanitizedRequiredString,
  tier: z.enum(["Enterprise", "Professional", "Standard"]),
  status: z.enum(["Active", "Trial"]),
});

export function ClientsPage() {
  const navigate = useNavigate();
  const { clients: localClients, addClient } = useEventsAdminDemo();

  // GraphQL: fetch clients from mock server
  const { data: gqlData } = useQuery<{ clients: typeof localClients }>(
    GET_CLIENTS,
    { fetchPolicy: "network-only" },
  );
  const clients = gqlData?.clients ?? localClients;
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<
    "All" | "Active" | "Trial"
  >("All");
  const [isAddClientOpen, setIsAddClientOpen] = useState(false);

  const addClientForm = useValidatedForm(addClientSchema, {
    defaultValues: {
      name: "",
      tier: "Standard" as const,
      status: "Trial" as const,
    },
  });

  const handleAddClient = (values: z.infer<typeof addClientSchema>) => {
    addClient(values);
    addClientForm.reset();
    setIsAddClientOpen(false);
  };

  const visibleClients = useMemo(
    () =>
      clients.filter((client) => {
        const matchesSearch = client.name
          .toLowerCase()
          .includes(searchTerm.trim().toLowerCase());
        const matchesStatus =
          selectedStatus === "All" ? true : client.status === selectedStatus;

        return matchesSearch && matchesStatus;
      }),
    [clients, searchTerm, selectedStatus],
  );

  const totals = useMemo(
    () => ({
      totalClients: clients.length,
      activeEvents: clients.reduce((sum, client) => sum + client.events, 0),
      totalOrganizers: clients.reduce(
        (sum, client) => sum + client.organizers,
        0,
      ),
      totalTravelers: clients.reduce(
        (sum, client) => sum + client.travelers,
        0,
      ),
    }),
    [clients],
  );

  return (
    <section className="events-page events-page--stack">
      <header className="events-page__hero">
        <div>
          <p className="events-page__eyebrow">
            {formatOneworld("oneworld Admin")}
          </p>
          <h1 className="events-page__title">Client Management</h1>
        </div>

        <Button
          size="lg"
          color="primary"
          className="events-action-button"
          onPress={() => setIsAddClientOpen(true)}
        >
          <PlusIcon className="size-4" />
          Add New Client
        </Button>
      </header>

      <article className="events-surface events-surface--search">
        <div className="events-search-bar">
          <SearchIcon className="size-4" />
          <InputBase
            aria-label="Search clients by name"
            value={searchTerm}
            onChange={(value: string | ChangeEvent<HTMLInputElement>) =>
              setSearchTerm(
                typeof value === "string" ? value : value.target.value,
              )
            }
            placeholder="Search clients by name..."
          />
        </div>
        <div
          className="events-chip-group"
          role="tablist"
          aria-label="Client status filters"
        >
          {(["All", "Active", "Trial"] as const).map((status) => (
            <button
              key={status}
              type="button"
              role="tab"
              aria-selected={selectedStatus === status}
              className={`events-chip-tab ${selectedStatus === status ? "is-active" : ""}`}
              onClick={() => setSelectedStatus(status)}
            >
              {status}
            </button>
          ))}
        </div>
      </article>

      <div className="events-stat-grid">
        <article className="events-stat-card">
          <header>
            <span>Total Clients</span>
            <div className="events-stat-card__icon">
              <UserGroupIcon className="size-4" />
            </div>
          </header>
          <strong>{totals.totalClients}</strong>
        </article>
        <article className="events-stat-card">
          <header>
            <span>Active Events</span>
            <div className="events-stat-card__icon">
              <UserGroupIcon className="size-4" />
            </div>
          </header>
          <strong>{totals.activeEvents}</strong>
        </article>
        <article className="events-stat-card">
          <header>
            <span>Total Organizers</span>
            <div className="events-stat-card__icon">
              <UserGroupIcon className="size-4" />
            </div>
          </header>
          <strong>{totals.totalOrganizers}</strong>
        </article>
        <article className="events-stat-card">
          <header>
            <span>Total Travelers</span>
            <div className="events-stat-card__icon">
              <UserGroupIcon className="size-4" />
            </div>
          </header>
          <strong>{totals.totalTravelers.toLocaleString()}</strong>
        </article>
      </div>

      <article className="events-surface events-surface--table">
        <header className="events-page__hero events-page__hero--table">
          <h2 className="events-surface__title">Active Clients</h2>
        </header>

        <table className="events-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Tier</th>
              <th>Status</th>
              <th>Events</th>
              <th>Organizers</th>
              <th>Travelers</th>
              <th>Last Activity</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleClients.map((client) => (
              <tr key={client.id}>
                <td>
                  <strong className="events-table__primary-link">
                    {client.name}
                  </strong>
                  <span className="events-table__caption">{client.slug}</span>
                </td>
                <td>
                  <span
                    className={`events-tone ${client.tier === "Enterprise" ? "events-tone--premium" : "events-tone--info"}`}
                  >
                    {client.tier}
                  </span>
                </td>
                <td>
                  <span
                    className={
                      client.status === "Trial"
                        ? "events-tone events-tone--warning"
                        : "events-tone events-tone--success"
                    }
                  >
                    {client.status}
                  </span>
                </td>
                <td>{client.events}</td>
                <td>{client.organizers}</td>
                <td>{client.travelers.toLocaleString()}</td>
                <td>{client.lastActivity}</td>
                <td>
                  <Button
                    size="sm"
                    color="primary"
                    className="events-pill-button"
                    onPress={() => navigate("/settings")}
                  >
                    Configure
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </article>

      <ModalOverlay isOpen={isAddClientOpen} onOpenChange={setIsAddClientOpen}>
        <Modal className="events-modal events-modal--compact">
          <Dialog className="events-dialog" aria-label="Add new client">
            <div className="events-modal__header">
              <h3>Add New Client</h3>
              <button
                type="button"
                className="events-dialog__close"
                onClick={() => setIsAddClientOpen(false)}
                aria-label="Close modal"
              >
                <CloseIcon className="size-4" />
              </button>
            </div>

            <ValidatedForm
              form={addClientForm}
              onSubmit={handleAddClient}
              className="events-form-stack"
            >
              <ValidatedInput
                form={addClientForm}
                name="name"
                label="Client Name"
                placeholder="Enter client organization name"
              />

              <Controller
                control={addClientForm.control}
                name="tier"
                render={({ field }) => (
                  <div className="events-form-row events-form-row--full">
                    <label className="events-form-label" htmlFor="client-tier">
                      Tier
                    </label>
                    <Select
                      id="client-tier"
                      selectedKey={field.value}
                      onSelectionChange={(key) => field.onChange(String(key))}
                      items={[
                        { id: "Enterprise", label: "Enterprise" },
                        { id: "Professional", label: "Professional" },
                        { id: "Standard", label: "Standard" },
                      ]}
                      placeholder="Select tier"
                    >
                      {(item) => (
                        <Select.Item id={item.id} textValue={item.label}>
                          {item.label}
                        </Select.Item>
                      )}
                    </Select>
                  </div>
                )}
              />

              <Controller
                control={addClientForm.control}
                name="status"
                render={({ field }) => (
                  <div className="events-form-row events-form-row--full">
                    <label
                      className="events-form-label"
                      htmlFor="client-status"
                    >
                      Status
                    </label>
                    <Select
                      id="client-status"
                      selectedKey={field.value}
                      onSelectionChange={(key) => field.onChange(String(key))}
                      items={[
                        { id: "Active", label: "Active" },
                        { id: "Trial", label: "Trial" },
                      ]}
                      placeholder="Select status"
                    >
                      {(item) => (
                        <Select.Item id={item.id} textValue={item.label}>
                          {item.label}
                        </Select.Item>
                      )}
                    </Select>
                  </div>
                )}
              />

              <div className="events-modal__footer">
                <Button
                  size="lg"
                  color="primary"
                  className="events-solid-button"
                  type="submit"
                >
                  Add Client
                </Button>
                <Button
                  size="lg"
                  color="secondary"
                  className="events-outline-button"
                  onPress={() => setIsAddClientOpen(false)}
                >
                  Cancel
                </Button>
              </div>
            </ValidatedForm>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </section>
  );
}
